// Contact section — saves to Firestore (or shows toast if not configured)
import { useState, useEffect } from "react";
import { Phone, MessageCircle, MapPin, Mail, Send, Loader2, Instagram, Facebook, Youtube } from "lucide-react";
import { toast } from "sonner";
import { PHONE_DISPLAY, WA_DISPLAY, PHONE_NUMBER, WHATSAPP_NUMBER, INSTAGRAM_URL, FACEBOOK_URL, YOUTUBE_URL } from "@/lib/cart";
import { submitContactMessage } from "@/lib/firestoreService";
import { firebaseReady } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/lib/i18n";
import { asset } from "@/lib/siteAssets";
import { track, trackNavClick } from "@/lib/analytics";

const SOCIAL = [
  { href: INSTAGRAM_URL, icon: Instagram, label: "Instagram" },
  { href: FACEBOOK_URL,  icon: Facebook,  label: "Facebook"  },
  { href: YOUTUBE_URL,   icon: Youtube,   label: "YouTube"   },
];

const WA_GREETING = encodeURIComponent(
  "Hello Annapurna Home Pickles, I would like to place an order."
);

export const Contact = () => {
  const { t } = useLang();
  const { user, profile } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  // Signed-in customers already have a known identity — auto-fill from their
  // profile and lock whichever of these fields already has a value, so they
  // only need to type the message itself. A field with no profile value yet
  // (e.g. phone never set) stays editable rather than trapping them behind a
  // required-but-locked-blank input.
  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      name: profile?.name || f.name,
      email: profile?.email || user?.email || f.email,
      phone: profile?.phone || f.phone,
    }));
  }, [user, profile]);

  const lockedName = !!(user && profile?.name);
  const lockedEmail = !!(user && (profile?.email || user?.email));
  const lockedPhone = !!(user && profile?.phone);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Please add your name and message");
      return;
    }
    if (!firebaseReady) {
      toast.info("Contact form will be saved once Firebase is connected. Please use WhatsApp meanwhile.");
      return;
    }
    setLoading(true);
    try {
      await submitContactMessage(form);
      track("contact_form_submitted", { has_phone: !!form.phone.trim(), has_email: !!form.email.trim() });
      toast.success("Message sent! We'll get back to you soon.");
      setForm((f) => (user ? { ...f, message: "" } : { name: "", email: "", phone: "", message: "" }));
    } catch (err) {
      toast.error("Could not send. " + (err?.message || ""));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-8 sm:py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-6">
          <div className="brand-ribbon mb-2">{t.contact.ribbon}</div>
          <h2 className="font-display text-xl sm:text-2xl lg:text-3xl font-bold text-[#5A2D1D]">
            {t.contact.h1} <span className="italic text-[#7A3B25]">{t.contact.h2}</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          <div>
            <div className="space-y-5">
              <a
                data-testid="contact-phone"
                href={`tel:+${PHONE_NUMBER}`}
                onClick={() => track("call_us_clicked", { source: "contact_page" })}
                className="flex items-center gap-5 p-5 bg-[#F5E6CC] border border-[#5A2D1D]/25 rounded-sm hover-lift"
              >
                <span className="w-14 h-14 rounded-full bg-[#5A2D1D] text-[#F5E6CC] grid place-items-center flex-shrink-0">
                  <Phone className="w-6 h-6" />
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold">{t.contact.call}</div>
                  <div className="font-display text-xl sm:text-2xl font-bold text-[#5A2D1D]">{PHONE_DISPLAY}</div>
                </div>
              </a>

              <a
                data-testid="contact-whatsapp"
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WA_GREETING}`}
                target="_blank"
                rel="noreferrer"
                onClick={() => track("whatsapp_contact_clicked", { source: "contact_page" })}
                className="flex items-center gap-5 p-5 bg-[#556B2F] text-[#F5E6CC] rounded-sm hover-lift"
              >
                <span className="w-14 h-14 rounded-full bg-[#F5E6CC] text-[#556B2F] grid place-items-center flex-shrink-0">
                  <MessageCircle className="w-6 h-6" />
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-widest opacity-80 font-bold">{t.contact.whatsapp}</div>
                  <div className="font-display text-xl sm:text-2xl font-bold">{WA_DISPLAY}</div>
                </div>
              </a>

              <div className="flex items-center gap-5 p-5 bg-[#F5E6CC] border border-[#5A2D1D]/25 rounded-sm">
                <span className="w-14 h-14 rounded-full bg-[#5A2D1D] text-[#F5E6CC] grid place-items-center flex-shrink-0">
                  <MapPin className="w-6 h-6" />
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold">{t.contact.made_in}</div>
                  <div className="font-display text-lg font-bold text-[#5A2D1D]">{t.contact.made_in_loc}</div>
                </div>
              </div>

              {/* Social media */}
              <div className="flex items-center gap-3 pt-1">
                {SOCIAL.map(({ href, icon: Icon, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={label}
                    title={label}
                    onClick={() => trackNavClick(label, "contact_page")}
                    className="w-11 h-11 rounded-full border border-[#5A2D1D]/25 text-[#5A2D1D]/70 hover:text-[#5A2D1D] hover:border-[#5A2D1D] flex items-center justify-center transition-colors"
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>

            <div
              data-testid="godavari-banner"
              className="mt-6 relative aspect-[16/9] rounded-sm overflow-hidden border border-[#5A2D1D]/25"
            >
              <img
                src={asset("brand/godavari.png")}
                alt="The Godavari river at Rajahmundry"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2E1A12]/80 via-[#2E1A12]/10 to-transparent" />
              <p className="absolute bottom-4 left-0 right-0 px-6 text-center font-display italic text-lg sm:text-xl font-bold text-[#F5E6CC] drop-shadow">
                Taste from the roots of Godavari
              </p>
            </div>
          </div>

          <div>
            <div
              data-testid="contact-full-address"
              className="mb-6 bg-[#F5E6CC] border border-[#5A2D1D]/25 rounded-sm p-5 flex items-start gap-4"
            >
              <span className="w-11 h-11 rounded-full bg-[#5A2D1D] text-[#F5E6CC] grid place-items-center flex-shrink-0">
                <MapPin className="w-5 h-5" />
              </span>
              <div className="text-sm text-[#2E1A12]">
                <div className="font-display text-base font-bold text-[#5A2D1D] mb-1">Annapurna Home Pickles</div>
                <div className="leading-relaxed text-[#2E1A12]/90">
                  CHIRUMANI SAI RAJA GUBBALA<br />
                  2-283/3, Kadiyapusavaram<br />
                  Kadiam, Rajahmundry Rural<br />
                  East Godavari District, Andhra Pradesh – 533101<br />
                  India
                </div>
              </div>
            </div>

          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="bg-[#F5E6CC] border border-[#5A2D1D]/25 rounded-sm p-6 sm:p-8 space-y-4"
          >
            <h3 className="font-display text-2xl font-bold text-[#5A2D1D] mb-2 flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#556B2F]" /> {t.contact.form_title}
            </h3>
            <Input testid="contact-name-input" label={t.contact.name} value={form.name} onChange={(v) => setForm({ ...form, name: v })} required disabled={lockedName} />
            <div className="grid sm:grid-cols-2 gap-4">
              <Input testid="contact-phone-input" label={t.contact.phone} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} disabled={lockedPhone} />
              <Input testid="contact-email-input" type="email" label={t.contact.email} value={form.email} onChange={(v) => setForm({ ...form, email: v })} disabled={lockedEmail} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold">{t.contact.message}</label>
              <textarea
                data-testid="contact-message-input"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={5}
                required
                className="mt-1 w-full px-4 py-3 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none resize-none"
              />
            </div>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] disabled:opacity-60 text-[#F5E6CC] py-4 rounded-sm font-semibold transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {t.contact.send}
            </button>
          </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Input = ({ label, testid, type = "text", value, onChange, required, disabled }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold">{label}</label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      readOnly={disabled}
      className={`mt-1 w-full px-4 py-3 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
    />
  </div>
);
